import DOMPurify from "dompurify";

const namedEntities: Record<string, string> = {
  amp: "&",
  apos: "'",
  gt: ">",
  lt: "<",
  nbsp: "\u00a0",
  quot: '"'
};
const allowedStyleProperties = new Set([
  "background-color",
  "color",
  "font-family",
  "font-size",
  "font-style",
  "font-weight",
  "text-align",
  "text-decoration"
]);
const unsafeStyleValuePattern = /(?:expression|url|var)\s*\(|[<>{}\\]/i;

function decodeEntities(value: string): string {
  return value.replace(
    /&#(\d+);|&#x([0-9a-f]+);|&(amp|apos|gt|lt|nbsp|quot);/gi,
    (entity, decimal: string, hexadecimal: string, named: string) => {
      if (decimal || hexadecimal) {
        const codePoint = Number.parseInt(decimal || hexadecimal, decimal ? 10 : 16);
        if (codePoint < 0 || codePoint > 0x10ffff || (codePoint >= 0xd800 && codePoint <= 0xdfff)) {
          return entity;
        }
        return String.fromCodePoint(codePoint);
      }
      return namedEntities[named.toLowerCase()] ?? entity;
    }
  );
}

function restrictInlineStyles(element: HTMLElement): void {
  for (const property of Array.from(element.style)) {
    const value = element.style.getPropertyValue(property);
    if (!allowedStyleProperties.has(property) || unsafeStyleValuePattern.test(value)) {
      element.style.removeProperty(property);
    }
  }

  if (element.style.length === 0) {
    element.removeAttribute("style");
  }
}

export function sanitizeNotificationHtml(value: string): string {
  const decodedContent = decodeEntities(decodeEntities(value));
  const sanitizedContent = DOMPurify.sanitize(decodedContent, {
    ALLOWED_TAGS: [
      "a",
      "b",
      "blockquote",
      "br",
      "code",
      "div",
      "em",
      "h1",
      "h2",
      "h3",
      "h4",
      "h5",
      "h6",
      "i",
      "li",
      "ol",
      "p",
      "pre",
      "s",
      "span",
      "strike",
      "strong",
      "sub",
      "sup",
      "u",
      "ul"
    ],
    ALLOWED_ATTR: ["href", "rel", "style", "target"],
    ALLOW_DATA_ATTR: false
  });
  const template = document.createElement("template");
  template.innerHTML = sanitizedContent;

  template.content.querySelectorAll<HTMLElement>("[style]").forEach(restrictInlineStyles);
  template.content.querySelectorAll<HTMLAnchorElement>("a").forEach((link) => {
    if (link.target === "_blank") {
      link.rel = "noopener noreferrer";
    }
  });

  return template.innerHTML;
}
